import React, { useState, useEffect, useRef } from 'react';
import { formatFileSize } from '../../utils/fileFormatting';
import {
  CloudArrowUpIcon,
  XMarkIcon,
  DocumentIcon,
  MusicalNoteIcon,
  VideoCameraIcon,
  PhotoIcon,
  PauseIcon,
} from '@heroicons/react/24/outline';
import { ChevronUpIcon, ChevronDownIcon } from '@heroicons/react/24/solid';
import type { UploadFile } from '../../contexts/UploadQueueContext';

interface UploadQueuePanelProps {
  uploads: UploadFile[];
  onRetry: (uploadId: string) => void;
  onRemove: (uploadId: string) => void;
  onPause: (uploadId: string) => void;
  onResume: (uploadId: string) => void;
  onPauseAll: () => void;
  onClear: () => void;
}

// Floating upload queue rendered once at app level so it survives tab and
// view changes while uploads are in flight.
const UploadQueuePanel: React.FC<UploadQueuePanelProps> = ({
  uploads,
  onRetry,
  onRemove,
  onPause,
  onResume,
  onPauseAll,
  onClear,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const onClearRef = useRef(onClear);

  const handleClose = () => {
    setIsVisible(false);
    // Clear finished uploads after the hiding animation
    setTimeout(() => {
      onClearRef.current();
    }, 300);
  };

  useEffect(() => {
    onClearRef.current = onClear;
  }, [onClear]);

  const completedUploads = uploads.filter(u => u.status === 'completed').length;
  const failedUploads = uploads.filter(u => u.status === 'error').length;
  const activeUploads = uploads.filter(u => ['uploading', 'processing', 'pending'].includes(u.status)).length;
  const totalProgress = uploads.length > 0 ? uploads.reduce((acc, upload) => acc + upload.progress, 0) / uploads.length : 0;

  useEffect(() => {
    setIsVisible(uploads.length > 0);
    if (uploads.length > 0 && !isExpanded) {
      setIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads.length]);

  const getOverallStatus = () => {
    if (activeUploads > 0) return 'Uploading';
    if (failedUploads > 0 && completedUploads === 0) return 'Failed';
    if (failedUploads > 0) return 'Partial';
    return 'Complete';
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 transition-colors duration-slow ease-out opacity-100 transform translate-y-0">
      <div className={`bg-surface rounded-lg shadow-2xl overflow-hidden transition-all duration-slow ease-out ${
        isExpanded ? 'w-[28rem]' : 'w-80'
      }`}>
        {/* Header */}
        <div
          className="bg-surface-sunken p-4 cursor-pointer transition-colors duration-base"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {/* Status icon */}
              <div className={`w-10 h-10 rounded-md flex items-center justify-center ${
                getOverallStatus() === 'Complete'
                  ? 'bg-emerald-500'
                  : activeUploads > 0
                    ? 'bg-blue-500'
                    : getOverallStatus() === 'Failed'
                      ? 'bg-red-500'
                      : 'bg-gray-400'
              }`}>
                <CloudArrowUpIcon className="h-5 w-5 text-white" />
              </div>

              <div>
                <h3 className="font-semibold text-sm text-fg">
                  {activeUploads > 0 ? 'Uploading Files' :
                   getOverallStatus() === 'Complete' ? 'Upload Complete' :
                   getOverallStatus() === 'Failed' ? 'Upload Failed' :
                   'Upload Status'}
                </h3>
                <p className="text-xs text-fg-secondary mt-0.5">
                  {activeUploads > 0 ? `${completedUploads} of ${uploads.length} complete` :
                   `${completedUploads} files processed`}
                </p>
              </div>
            </div>

            {/* Right side controls */}
            <div className="flex items-center space-x-3">
              {/* Progress percentage */}
              {activeUploads > 0 && (
                <div className="text-sm font-bold text-fg">
                  {Math.round(totalProgress)}%
                </div>
              )}

              {/* Action buttons */}
              <div className="flex items-center space-x-1">
                {activeUploads > 0 && (
                  <button
                    aria-label="Pause all"
                    onClick={(e) => { e.stopPropagation(); onPauseAll(); }}
                    className="p-2 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition-colors duration-base"
                    title="Pause all"
                  >
                    <PauseIcon className="h-4 w-4 text-warning-text" />
                  </button>
                )}
                <button
                  aria-label="Close"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClose();
                  }}
                  className="p-2 hover:bg-surface-sunken rounded-lg transition-colors duration-base"
                  title="Close"
                >
                  <XMarkIcon className="h-4 w-4 text-fg-secondary" />
                </button>
                <div className="p-2">
                  {isExpanded ? (
                    <ChevronDownIcon className="h-4 w-4 text-fg-secondary" />
                  ) : (
                    <ChevronUpIcon className="h-4 w-4 text-fg-secondary" />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Overall progress bar */}
        {activeUploads > 0 && (
          <div className="w-full h-1 bg-surface-sunken">
            <div
              className="h-1 bg-blue-500 transition-colors duration-slow"
              style={{ width: `${totalProgress}%` }}
            />
          </div>
        )}

        {/* Upload List */}
        <div className={`overflow-hidden transition-all duration-slow ease-out ${
          isExpanded ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'
        }`}>
          <div className="bg-surface">
            <div className="max-h-72 overflow-y-auto">
              {uploads.map((upload, index) => (
                <div key={upload.id} className={`p-4 hover:bg-surface-sunken transition-colors ${
                  index !== uploads.length - 1 ? 'border-b border-subtle' : ''
                }`}>
                  <div className="flex items-start space-x-3">
                    {/* File type icon */}
                    <div className={`p-2 rounded-lg shrink-0 ${
                      upload.file.type.startsWith('audio/') ? 'bg-accent-muted dark:bg-accent-subtle' :
                      upload.file.type.startsWith('video/') ? 'bg-blue-100 dark:bg-blue-900/20' :
                      upload.file.type.startsWith('image/') ? 'bg-amber-100 dark:bg-amber-900/20' :
                      'bg-surface-sunken'
                    }`}>
                      {(() => {
                        const iconClass = "h-4 w-4";
                        if (upload.file.type.startsWith('audio/')) return <MusicalNoteIcon className={`${iconClass} text-accent`} />;
                        if (upload.file.type.startsWith('video/')) return <VideoCameraIcon className={`${iconClass} text-info-text`} />;
                        if (upload.file.type.startsWith('image/')) return <PhotoIcon className={`${iconClass} text-warning-text`} />;
                        return <DocumentIcon className={`${iconClass} text-fg-secondary`} />;
                      })()}
                    </div>

                    <div className="flex-1 min-w-0">
                      {/* File info row */}
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-sm font-medium text-fg truncate">
                            {upload.file.name}
                          </p>
                          <div className="flex items-center space-x-3 mt-1">
                            <span className="text-xs text-fg-secondary">
                              {formatFileSize(upload.file.size)}
                            </span>
                            <span className={`text-xs font-medium ${
                              upload.status === 'completed' ? 'text-emerald-600 dark:text-emerald-400' :
                              upload.status === 'error' ? 'text-danger-text' :
                              upload.status === 'uploading' ? 'text-info-text' :
                              upload.status === 'paused' ? 'text-warning-text' :
                              'text-fg-secondary'
                            }`}>
                              {upload.status === 'uploading' ? `Uploading • ${upload.progress}%` :
                               upload.status === 'processing' ? 'Processing' :
                               upload.status === 'completed' ? 'Complete' :
                               upload.status === 'error' ? 'Failed' :
                               upload.status === 'paused' ? 'Paused' :
                               'Waiting'}
                            </span>
                          </div>
                        </div>

                        <button
                          aria-label="Remove file"
                          onClick={() => onRemove(upload.id)}
                          className="p-1 hover:bg-surface-sunken rounded-lg transition-colors"
                        >
                          <XMarkIcon className="h-4 w-4 text-fg-tertiary hover:text-fg-secondary" />
                        </button>
                      </div>

                      {/* Progress bar */}
                      <div className="w-full bg-surface-sunken rounded-full h-1.5 overflow-hidden">
                        <div
                          className={`h-1.5 transition-all duration-slow ${
                            upload.status === 'completed' ? 'bg-emerald-500' :
                            upload.status === 'error' ? 'bg-red-500' :
                            upload.status === 'paused' ? 'bg-amber-500' :
                            'bg-blue-500'
                          }`}
                          style={{ width: `${upload.progress}%` }}
                        />
                      </div>

                      {/* Error message */}
                      {upload.error && (
                        <div className="mt-2 text-xs text-danger-text">
                          {upload.error}
                        </div>
                      )}

                      {/* Action buttons */}
                      {(upload.status === 'error' || upload.status === 'paused' || upload.status === 'uploading') && (
                        <div className="flex items-center space-x-2 mt-2">
                          {upload.status === 'error' && (
                            <button
                              onClick={() => onRetry(upload.id)}
                              className="text-xs font-medium text-info-text hover:text-info-text"
                            >
                              Retry
                            </button>
                          )}
                          {upload.status === 'paused' && (
                            <button
                              onClick={() => onResume(upload.id)}
                              className="text-xs font-medium text-emerald-600 hover:text-emerald-700 dark:text-emerald-400"
                            >
                              Resume
                            </button>
                          )}
                          {upload.status === 'uploading' && (
                            <button
                              onClick={() => onPause(upload.id)}
                              className="text-xs font-medium text-warning-text hover:text-warning-text"
                            >
                              Pause
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadQueuePanel;
